/**
 * SignInModal
 *
 * Unified sign-in modal used across the app.
 * Replaces both SavedItemsSignInModal and SignUpPromptModal with configurable props.
 *
 * Variants:
 * - Default: Basic sign-in prompt
 * - Saved items: Title/subtitle for collections feature
 */
"use client";

import React, { useState } from "react";

import { useRouter } from "next/navigation";

import { useAuth } from "@/contexts/AuthContext";
import { useOnboardingModal } from "@/contexts/OnboardingModalContext";
import { consumePendingAuthRedirect } from "@/lib/pending-auth-redirect";
import { colors, modalSpacing } from "@/styles/design-tokens";

import { BasicModalShell } from "./BasicModalShell";
import { FloatingLabelInput } from "./FloatingLabelInput";
import { ModalPrimaryButton } from "./ModalPrimaryButton";
import { OAuthButton } from "./OAuthButton";
import { TextArrowButton } from "./TextArrowButton";

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Modal title (defaults to "Sign In") */
  title?: string;
  /** Optional subtitle below title */
  subtitle?: string;
  /** Where to redirect after successful login (defaults to "/") */
  redirectPath?: string;
  /** Action context for analytics/logging */
  action?: "cart" | "collection" | "general" | "brand" | "profile";
  /** Custom close button positioning */
  closeButtonClassName?: string;
}

export function SignInModal({
  isOpen,
  onClose,
  title = "Sign In",
  subtitle,
  redirectPath = "/",
  action = "general",
  closeButtonClassName,
}: SignInModalProps) {
  const router = useRouter();
  const { login, loginWithGoogle, loginWithApple, resetPassword } = useAuth();
  const { startOnboarding } = useOnboardingModal();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const isLoading = isSigningIn || isResettingPassword;

  // Adjust top padding based on whether we have a subtitle
  const topPadding = subtitle ? "clamp(32px, 10vh, 90px)" : "clamp(28px, 8vh, 70px)";

  // Close any open product modals when this modal opens
  React.useEffect(() => {
    if (isOpen) {
      // NOTE: This dispatches an Escape key event to close any open product modals
      // before showing the sign-in modal. This is a workaround for coordinating
      // multiple modal states. Consider using a modal management context in the future.
      const escapeEvent = new KeyboardEvent("keydown", {
        key: "Escape",
        code: "Escape",
        keyCode: 27,
        bubbles: true,
        cancelable: true,
      });
      document.dispatchEvent(escapeEvent);
      // Reset state when modal opens
      setError("");
      setNotice("");
      setEmail("");
      setPassword("");
    }
  }, [isOpen, action]);

  const navigateAfterAuth = () => {
    const nextPath = consumePendingAuthRedirect() ?? redirectPath;
    router.push(nextPath);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setIsSigningIn(true);
    setError("");
    setNotice("");

    try {
      await login(email, password);
      onClose();
      navigateAfterAuth();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to sign in. Please check your credentials.";
      setError(errorMessage);
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleSignUp = () => {
    onClose();
    // Wait for modal close animation to complete before opening onboarding
    setTimeout(() => {
      startOnboarding("signup");
    }, 200);
  };

  const handleGoogleAuth = async () => {
    setError("");
    setNotice("");
    setIsSigningIn(true);

    try {
      const result = await loginWithGoogle();
      if (result.status === "cancelled") {
        setIsSigningIn(false);
        return;
      }
      // On desktop (popup flow), this line executes after successful auth
      // On mobile (redirect flow), the page redirects so this won't run
      setIsSigningIn(false);
      onClose();
      if (result.status === "success") {
        navigateAfterAuth();
      }
    } catch (err) {
      console.error("[SignInModal] Google auth error:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to sign in with Google.";
      setError(errorMessage);
      setIsSigningIn(false);
    }
  };

  const handleAppleAuth = async () => {
    setError("");
    setNotice("");
    setIsSigningIn(true);

    try {
      const result = await loginWithApple();
      if (result.status === "cancelled") {
        setIsSigningIn(false);
        return;
      }
      // On desktop (popup flow), this line executes after successful auth
      // On mobile (redirect flow), the page redirects so this won't run
      setIsSigningIn(false);
      onClose();
      if (result.status === "success") {
        navigateAfterAuth();
      }
    } catch (err) {
      console.error("[SignInModal] Apple auth error:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to sign in with Apple.";
      setError(errorMessage);
      setIsSigningIn(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setNotice("");
      setError("Enter your email address first.");
      return;
    }

    setError("");
    setNotice("");
    setIsResettingPassword(true);

    try {
      await resetPassword(email);
      setNotice(
        "If an account exists for that email, we sent a reset link. If you don't see it, check your spam folder."
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "We couldn't send a reset email. Please try again.";
      setError(errorMessage);
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Determine title class based on presence of subtitle
  const titleClass = subtitle ? "text-title-lg" : "text-title";

  return (
    <BasicModalShell
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={true}
      closeButtonClassName={closeButtonClassName}
    >
      <div
        data-testid="sign-in-modal"
        className="mx-auto flex w-full flex-col px-4 sm:px-0"
        style={{
          maxWidth: modalSpacing.signInContentWidth,
          paddingTop: topPadding,
          paddingBottom: modalSpacing.contentBottomPadding,
        }}
      >
        {/* Header with Title and optional Subtitle */}
        <div
          className="flex flex-col text-center"
          style={{
            gap: subtitle ? modalSpacing.headerGap : undefined,
            marginBottom: modalSpacing.sectionGap,
          }}
        >
          <h2 data-testid="sign-in-modal-title" className={`${titleClass} text-ink`}>
            {title}
          </h2>
          {subtitle && (
            <p data-testid="sign-in-modal-subtitle" className="text-title text-ink-muted">
              {subtitle}
            </p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="rounded border p-3"
            style={{
              backgroundColor: colors.feedback.errorBackground,
              borderColor: colors.feedback.errorBorder,
              marginBottom: modalSpacing.sectionGap,
            }}
          >
            <p
              className="text-input-modal text-center"
              style={{
                color: colors.feedback.errorTextPrimary,
                fontSize: "12px",
              }}
            >
              {error}
            </p>
          </div>
        )}

        {notice && (
          <div
            className="rounded border p-3"
            style={{
              backgroundColor: colors.feedback.noticeBackground,
              borderColor: colors.feedback.noticeBorder,
              marginBottom: modalSpacing.sectionGap,
            }}
          >
            <p
              className="text-input-modal text-center"
              style={{
                color: colors.feedback.noticeTextPrimary,
                fontSize: "12px",
              }}
            >
              {notice}
            </p>
          </div>
        )}

        {/* Form */}
        <form
          onSubmit={handleSignIn}
          className="flex flex-col"
          style={{
            gap: modalSpacing.formFieldGap,
            marginBottom: modalSpacing.formToOauthGap,
          }}
        >
          <FloatingLabelInput
            type="email"
            value={email}
            onChange={setEmail}
            label="Email address"
          />

          <FloatingLabelInput
            type="password"
            value={password}
            onChange={setPassword}
            label="Password"
          />

          <button
            type="button"
            onClick={handleResetPassword}
            disabled={isLoading}
            className="self-start text-left transition-opacity hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="text-overline text-ink">Forgot Password?</span>
          </button>

          <ModalPrimaryButton type="submit" disabled={isLoading || !email || !password}>
            {isSigningIn ? "Signing In..." : "Sign In"}
          </ModalPrimaryButton>
        </form>

        {/* OAuth Buttons */}
        <div
          className="flex flex-col"
          style={{
            gap: modalSpacing.oauthButtonGap,
            marginBottom: modalSpacing.sectionGap,
          }}
        >
          <OAuthButton provider="google" onClick={handleGoogleAuth} disabled={isLoading} />
          <OAuthButton provider="apple" onClick={handleAppleAuth} disabled={isLoading} />
        </div>

        {/* Sign Up Section */}
        <div
          className="flex flex-col items-center border-t"
          style={{
            borderColor: colors.divider.divider1,
            paddingTop: modalSpacing.footerSectionPadding,
            gap: modalSpacing.footerGap,
          }}
        >
          <p
            className="text-input-modal text-center"
            style={{ color: colors.text.floatingLabelInactive }}
          >
            Don't have an account?
          </p>
          <TextArrowButton label="Create An Account" onClick={handleSignUp} />
        </div>
      </div>
    </BasicModalShell>
  );
}
